import { useEffect } from 'react';
import { create } from 'zustand';
import {
  isAuthenticated,
  refreshAccessToken,
  revokeTokens,
  exchangeCodeForTokens,
  getReturnPath,
  clearReturnPath,
  hydrateAccessTokenFromStorage,
  isAuthCallbackLocation,
} from 'utils/authUtils';
import { ROUTES } from 'utils/constants';
import { devlogs } from 'utils/devlogs';

interface AuthStore {
  isAuth: boolean;
  isLoading: boolean;
  checkAuth: () => Promise<void>;
  logout: () => Promise<void>;
  handleAuthCallback: (code: string, state?: string) => Promise<string>;
}

/** Deduplicate OAuth callback handling (e.g. React Strict Mode double-invokes effects). */
let authCallbackInFlight: Promise<string> | null = null;

export const useAuthStore = create<AuthStore>((set) => ({
  isAuth: false,
  isLoading: true,

  checkAuth: async () => {
    // On OAuth redirect we still have PKCE values in sessionStorage. The normal
    // unauthenticated path calls refreshAccessToken(), which 401s and clearTokens()
    // — that wipes oauth_state / oauth_code_verifier before AuthCallback runs.
    if (isAuthCallbackLocation()) {
      await hydrateAccessTokenFromStorage();
      if (isAuthenticated()) {
        set({ isAuth: true, isLoading: false });
      } else {
        set({ isAuth: false, isLoading: false });
      }
      return;
    }

    await hydrateAccessTokenFromStorage();

    if (isAuthenticated()) {
      set({ isAuth: true, isLoading: false });
      try {
        await refreshAccessToken();
      } catch {
        set({ isAuth: false });
      }
      return;
    }

    try {
      await refreshAccessToken();
      set({ isAuth: true, isLoading: false });
    } catch {
      set({ isAuth: false, isLoading: false });
    }
  },

  handleAuthCallback: async (code: string, state?: string): Promise<string> => {
    if (authCallbackInFlight) {
      devlogs(
        '[auth/store]',
        'handleAuthCallback: awaiting in-flight exchange',
      );
      return authCallbackInFlight;
    }

    authCallbackInFlight = (async (): Promise<string> => {
      try {
        set({ isLoading: true });
        devlogs('[auth/store]', 'handleAuthCallback: start', {
          codeLength: code.length,
          hasState: Boolean(state),
        });
        await exchangeCodeForTokens(code, state);
        const authenticated = isAuthenticated();
        devlogs('[auth/store]', 'after exchangeCodeForTokens', {
          authenticated,
        });

        if (authenticated) {
          const returnPath = getReturnPath();
          clearReturnPath();
          set({ isAuth: true, isLoading: false });
          const next = returnPath || ROUTES.HOME;
          devlogs('[auth/store]', 'handleAuthCallback: success', {
            returnPath: returnPath ?? null,
            next,
          });
          return next;
        }
        throw new Error('Tokens were not stored correctly');
      } catch (error) {
        console.error('Failed to handle auth callback:', error);
        devlogs('[auth/store]', 'handleAuthCallback: error', error);
        set({ isAuth: false, isLoading: false });
        throw error;
      } finally {
        authCallbackInFlight = null;
      }
    })();

    return authCallbackInFlight;
  },

  logout: async () => {
    try {
      set({ isLoading: true });
      await revokeTokens();
      await new Promise((resolve) => setTimeout(resolve, 100));
      set({ isAuth: false });
    } catch (error) {
      console.error('Failed to revoke tokens:', error);
      set({ isAuth: false });
    } finally {
      setTimeout(() => set({ isLoading: false }), 200);
    }
  },
}));

let hasInitialized = false;

export function useAuthInit(): void {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    if (hasInitialized) return;
    hasInitialized = true;
    void checkAuth();
  }, [checkAuth]);
}
